/**
 * TP-AT.RS.8.2 (Feb 2026)
 * =======================
 * Shared helper -- reused by the Roster cell editor, Bulk Add
 * and the Rota Style cell editor.
 *
 * Converts a saved shift template into the editor-ready row
 * list:
 *
 *   template envelope 09:00-17:00 with Break 13:00-14:00
 *   =>
 *   [ blank Work 09:00-13:00,
 *     Break     13:00-14:00,
 *     blank Work 14:00-17:00 ]
 *
 * Blank Work rows carry `category_id: ""` AND
 * `_wanted_kind: "work"` so:
 *   - the activity picker filters to Work categories only,
 *   - the save-time validation refuses to save until the
 *     manager picks an activity,
 *   - reporting cannot classify a blank row as Work because it
 *     has no `category_id`.
 *
 * Overnight envelopes (end <= start) are normalised to +24h so
 * gap math stays honest for shifts like 23:00-07:00.
 *
 * This module has NO React / DOM dependencies so it is trivial
 * to unit-test from Node.
 */

const _t2m = (t) => {
  if (!t || typeof t !== "string") return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const _m2t = (n) => {
  const w = ((n % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(w / 60)).padStart(2, "0")}:${String(w % 60).padStart(2, "0")}`;
};

export function deriveBlankWorkGapEntries(shiftStart, shiftEnd, tplEntries) {
  const sMin = _t2m(shiftStart);
  let eMin = _t2m(shiftEnd);
  if (sMin == null || eMin == null) {
    // Malformed envelope; return template entries untouched so
    // the manager can still edit manually.
    return (tplEntries || []).slice();
  }
  if (eMin <= sMin) eMin += 24 * 60; // overnight normalisation

  // Only entries with explicit clock windows contribute to
  // occupied ranges. Entries without start/end times keep the
  // legacy passthrough behaviour so pre-Feb 2026 custom
  // templates keep working unchanged.
  const ranges = [];
  for (const e of tplEntries || []) {
    let es = _t2m(e.start_time);
    let ee = _t2m(e.end_time);
    if (es == null || ee == null) continue;
    if (ee <= es) ee += 24 * 60;
    if (es < sMin) {
      es += 24 * 60;
      ee += 24 * 60;
    }
    ranges.push([es, ee]);
  }
  ranges.sort((a, b) => a[0] - b[0]);

  const out = [];
  let cursor = sMin;
  for (const [rs, re] of ranges) {
    if (rs > cursor) {
      out.push({
        category_id: "",
        combo_id: null,
        combo_label: null,
        minutes: rs - cursor,
        start_time: _m2t(cursor),
        end_time: _m2t(rs),
        note: null,
        _wanted_kind: "work",
      });
    }
    cursor = Math.max(cursor, re);
  }
  if (cursor < eMin) {
    out.push({
      category_id: "",
      combo_id: null,
      combo_label: null,
      minutes: eMin - cursor,
      start_time: _m2t(cursor),
      end_time: _m2t(eMin),
      note: null,
      _wanted_kind: "work",
    });
  }

  const withTime = (tplEntries || []).filter(e => e.start_time && e.end_time);
  const withoutTime = (tplEntries || []).filter(e => !e.start_time || !e.end_time);
  const merged = [...withTime, ...out];
  merged.sort((a, b) => {
    const as = _t2m(a.start_time) ?? 0;
    const ae = as < sMin ? as + 24 * 60 : as;
    const bs = _t2m(b.start_time) ?? 0;
    const be = bs < sMin ? bs + 24 * 60 : bs;
    return ae - be;
  });
  return [...merged, ...withoutTime];
}
